<!-- components/inscriptions/PeriodMessages.vue -->
<script setup lang="ts">
import { computed, watch } from 'vue';

type Item = {
  id: string;
  message: string;
  date_debut?: string | null;
  date_fin?: string | null;
  lien_url?: string | null;
  lien_label?: string | null;
};
type ApiResponse = { now: string; items: Item[] };

const props = defineProps<{
  from: Date | string;
  to: Date | string;
  tz?: string;
}>();
const emit = defineEmits<{ (e: 'days-change', keys: string[]): void }>();

/* --- Helpers dates --- */
function dmyInTz(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}
function ymdInTz(d: Date, tz: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d); // -> YYYY-MM-DD
}
function toDate(input: Date | string): Date {
  return typeof input === 'string' ? new Date(input) : input;
}
function addUTC(d: Date, days: number): Date {
  const n = new Date(d);
  n.setUTCDate(n.getUTCDate() + days);
  return n;
}
function windowDayKeys(from: Date | string, to: Date | string, tz: string): string[] {
  const start = toDate(from);
  const end = toDate(to);
  const keys: string[] = [];
  for (let cur = new Date(start); cur <= end; cur = addUTC(cur, 1)) {
    keys.push(ymdInTz(cur, tz));
  }
  return keys;
}
function isoToYmdInTz(iso: string | null | undefined, tz: string): string | null {
  return iso ? ymdInTz(new Date(iso), tz) : null;
}
function isoToDmyInTz(iso: string | null | undefined, tz: string): string | null {
  return iso ? dmyInTz(new Date(iso), tz) : null;
}

/* --- Fenêtre + zone --- */
const zone = computed(() => props.tz || 'Europe/Paris');
const fromYMD = computed(() => ymdInTz(toDate(props.from), zone.value));
const toYMD = computed(() => ymdInTz(toDate(props.to), zone.value));
const windowRef = ref<{ from: string; to: string; tz: string }>({ from: '', to: '', tz: zone.value });

// Synchronise windowRef quand props changent (une seule MAJ par tick)
watch(
  () => [props.from, props.to, zone.value],
  ([f, t, z]) => {
    windowRef.value = { from: ymdInTz(toDate(f), z), to: ymdInTz(toDate(t), z), tz: z };
  },
  { immediate: true },
);

/* --- Fetch SSR (par défaut server:true) --- */
const { data, pending, error } = await useFetch<ApiResponse>('/api/messages/period', {
  // params: computed(() => ({ from: fromYMD.value, to: toYMD.value, tz: zone.value })),
  params: windowRef,
  key: computed(() => `msgs:${windowRef.value.from}:${windowRef.value.to}:${windowRef.value.tz}`),
  // key: computed(() => `msgs:${fromYMD.value}:${toYMD.value}:${zone.value}`),
  // server:true par défaut => SSR
  // watch: false,
  default: () => ({ now: new Date().toISOString(), items: [] }),
  dedupe: 'defer',
});

const messages = computed<Item[]>(() => data.value?.items ?? []);

/* --- Calcul des jours impactés (SSR + CSR) --- */
const impactedKeys = computed<string[]>(() => {
  const win = windowDayKeys(props.from, props.to, zone.value);
  const impacted = new Set<string>();
  for (const m of messages.value) {
    const startKey = isoToYmdInTz(m.date_debut ?? null, zone.value) ?? win[0];
    const endKey = isoToYmdInTz(m.date_fin ?? null, zone.value) ?? win[win.length - 1];
    for (const k of win) if (k >= startKey && k <= endKey) impacted.add(k);
  }
  return Array.from(impacted);
});

/* --- Émettre vers le parent (seulement côté client après hydratation) --- */
if (import.meta.client) {
  watch(impactedKeys, (keys) => emit('days-change', keys), { immediate: true });
}

/* --- Affichage plage (debug lisible) --- */
function fmtRange(start?: string | null, end?: string | null) {
  const fmt = (iso?: string | null) =>
    iso
      ? new Date(iso).toLocaleString(undefined, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null;
  const s = fmt(start),
    e = fmt(end);
  if (s && e) return `${s} → ${e}`;
  if (s) return `Dès ${s}`;
  if (e) return `Jusqu’au ${e}`;
  return null;
}
</script>

<template>
  <div class="space-y-2">
    <!-- <div v-if="pending" class="text-sm text-gray-500">Chargement…</div> -->
    <div v-if="error" class="text-sm text-rose-700">Erreur : {{ error.message || error }}</div>

    <div
      v-for="m in messages"
      :key="m.id"
      class="relative rounded-xl border border-gray-200 bg-beigeMedium px-8 py-1 shadow-sm"
    >
      <div class="mt-1 text-xs text-gray-500 flex gap-1 items-center absolute top-1 left-1">
        <font-awesome :icon="['fas', 'message']" style="font-size: 13px" class="text-ocreMedium" />
      </div>

      <div class="mt-1 text-xs text-gray-500 flex gap-1 items-center">
        <span>jusqu'au {{ isoToDmyInTz(m.date_fin, zone) || '—' }}</span>
        <!-- complet : du {{ isoToDmyInTz(m.date_debut, zone) || '—' }} au {{ isoToDmyInTz(m.date_fin, zone) || '—' }} -->
      </div>

      <div class="prose prose-md max-w-none" v-html="m.message"></div>

      <div v-if="m.lien_url" class="mt-2">
        <a
          :href="m.lien_url"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50"
        >
          {{ m.lien_label || 'En savoir plus' }}
        </a>
      </div>
    </div>

    <!-- <div v-if="!pending && messages.length === 0" class="text-sm text-gray-500">Aucun message.</div> -->
  </div>
</template>
